import { useHandler } from "@redotech/react-util/hook";
import { TotalWeightWidget } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { SetShippingOptionsActionStep } from "@redotech/redo-model/advanced-flow/oms/actions";
import {
  CARRIER_HAZMAT,
  Hazmat,
  HAZMAT_DISPLAY_LABELS,
  isSupportedHazmat,
} from "@redotech/redo-model/fulfillments/hazmat";
import {
  includesLibraryMail,
  SpecialRateEligibility,
} from "@redotech/redo-model/outbound-labels/outbound-labels";
import {
  Weight,
  WeightFormat,
  WeightUnit,
} from "@redotech/redo-model/outbound-labels/util";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext } from "react";
import { PartialWithRequiredType } from ".";

export const ShippingOptionsInput = memo(function ShippingOptionsInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetShippingOptionsActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetShippingOptionsActionStep>,
  ) => void;
}) {
  const team = useContext(TeamContext);
  // Handler for signature required
  const handleSignatureRequired = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      if (value === true) {
        setStep({
          ...step,
          shippingOptions: {
            ...step.shippingOptions,
            signatureRequired: true,
            adultSignatureRequired: false,
          },
        });
      } else {
        setStep({
          ...step,
          shippingOptions: {
            ...step.shippingOptions,
            signatureRequired: false,
          },
        });
      }
    }
  };

  // Handler for adult signature required
  const handleAdultSignatureRequired = (value: CheckboxValue) => {
    if (value === true) {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          adultSignatureRequired: true,
          signatureRequired: false,
        },
      });
    } else {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          adultSignatureRequired: false,
        },
      });
    }
  };

  // Handler for insurance
  const handleInsurance = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: { ...step.shippingOptions, includeInsurance: value },
      });
    }
  };

  // Handler for Media Mail eligibility
  const handleMediaMailEligibility = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...step.shippingOptions,
          specialRateEligibility: value
            ? // If the box is checked, the option should always include media mail
              includesLibraryMail(step.shippingOptions?.specialRateEligibility)
              ? SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
              : SpecialRateEligibility.MEDIA_MAIL
            : // If the box is unchecked, we leave the library mail option (if it's already included)
              includesLibraryMail(step.shippingOptions?.specialRateEligibility)
              ? SpecialRateEligibility.LIBRARY_MAIL
              : undefined,
        },
      });
    }
  };

  const handleHazmatToggle = (value: boolean) => {
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        hazmat: value ? Hazmat.CLASS_9_UNMARKED_LITHIUM : null,
      },
    });
  };

  const handleHazmatTypeChange = (value: Hazmat) => {
    setStep({
      ...step,
      shippingOptions: {
        ...step.shippingOptions,
        hazmat: value,
        // Set default dry ice weight when switching to dry ice
        dryIceWeight:
          value === Hazmat.CLASS_9_DRY_ICE
            ? step.shippingOptions?.dryIceWeight || {
                unit: WeightUnit.OUNCE,
                value: 6,
              }
            : undefined,
      },
    });
  };

  // Handler for dry ice weight changes
  const handleDryIceWeightChange = useHandler((weight: Weight) => {
    setStep({
      ...step,
      shippingOptions: { ...step.shippingOptions, dryIceWeight: weight },
    });
  });

  // Handler for Library Mail eligibility
  /*
  const handleLibraryMailEligibility = (value: CheckboxValue) => {
    if (typeof value === "boolean") {
      setStep({
        ...step,
        shippingOptions: {
          ...(step.shippingOptions || {}),
          specialRateEligibility: value
            ? // If the box is checked, the option should always include library mail
              includesMediaMail(step.shippingOptions?.specialRateEligibility)
              ? SpecialRateEligibility.MEDIA_MAIL_AND_LIBRARY_MAIL
              : SpecialRateEligibility.LIBRARY_MAIL
            : // If the box is unchecked, we leave the media mail option (if it's already included)
              includesMediaMail(step.shippingOptions?.specialRateEligibility)
              ? SpecialRateEligibility.MEDIA_MAIL
              : undefined,
        },
      });
    }
  };
  */

  return (
    <Flex dir="column" gap="md" mt="md">
      <RedoCheckbox
        description="Require a signature upon delivery."
        label="Signature required"
        setValue={handleSignatureRequired}
        showLabelSpacer
        size="xs"
        value={step.shippingOptions?.signatureRequired ?? false}
      />
      <RedoCheckbox
        description="Require an adult signature (18+) upon delivery."
        label="Adult signature required"
        setValue={handleAdultSignatureRequired}
        size="xs"
        value={step.shippingOptions?.adultSignatureRequired ?? false}
      />
      <RedoCheckbox
        description={`Purchase shipping insurance for ${team?.settings?.outboundLabels?.insuranceRatePercentage || 1}% of order value`}
        label="Add additional insurance"
        setValue={handleInsurance}
        size="xs"
        value={step.shippingOptions?.includeInsurance ?? false}
      />
      <Flex dir="column" gap="md" justify="flex-start">
        <RedoToggle
          label="This shipment contains hazardous materials"
          setValue={handleHazmatToggle}
          size="xs"
          value={step.shippingOptions?.hazmat != null}
        />

        {step.shippingOptions?.hazmat != null && (
          <Flex dir="column" gap="md" ml="xs" pl="4xl">
            <RedoSingleSelectDropdownInput<Hazmat>
              label="Hazmat type"
              options={[
                {
                  id: "fedex-dhl-section",
                  type: "section" as const,
                  text: "FedEx / DHL",
                  value: null as unknown as Hazmat,
                },
                ...CARRIER_HAZMAT.FEDEX_DHL.map((hazmat) => ({
                  id: hazmat,
                  type: "text" as const,
                  text: HAZMAT_DISPLAY_LABELS[hazmat],
                  value: hazmat,
                })),
                {
                  id: "usps-section",
                  type: "section" as const,
                  text: "USPS",
                  value: null as unknown as Hazmat,
                },
                ...CARRIER_HAZMAT.USPS.map((hazmat) => ({
                  id: hazmat,
                  type: "text" as const,
                  text: HAZMAT_DISPLAY_LABELS[hazmat],
                  value: hazmat,
                })),
                {
                  id: "all-carriers-section",
                  type: "section" as const,
                  text: "All Carriers",
                  value: null as unknown as Hazmat,
                },
                ...CARRIER_HAZMAT.ALL_CARRIERS.map((hazmat) => ({
                  id: hazmat,
                  type: "text" as const,
                  text: HAZMAT_DISPLAY_LABELS[hazmat],
                  value: hazmat,
                })),
              ]}
              optionSelected={({ item }) => {
                if (item?.value && item.type !== "section") {
                  handleHazmatTypeChange(item.value);
                }
              }}
              selectedItem={
                step.shippingOptions?.hazmat &&
                isSupportedHazmat(step.shippingOptions.hazmat)
                  ? {
                      id: step.shippingOptions.hazmat,
                      type: "text" as const,
                      text: HAZMAT_DISPLAY_LABELS[step.shippingOptions.hazmat],
                      value: step.shippingOptions.hazmat,
                    }
                  : undefined
              }
              size="xs"
            />

            {step.shippingOptions?.hazmat === Hazmat.CLASS_9_DRY_ICE && (
              <Flex pl="2xl" w="full">
                <TotalWeightWidget
                  disabled={false}
                  hideScale
                  onWeightChange={handleDryIceWeightChange}
                  title=""
                  weight={
                    step.shippingOptions?.dryIceWeight?.unit &&
                    step.shippingOptions?.dryIceWeight?.value
                      ? {
                          unit: step.shippingOptions?.dryIceWeight?.unit,
                          value: step.shippingOptions?.dryIceWeight?.value,
                        }
                      : { unit: WeightUnit.OUNCE, value: 6 }
                  }
                  weightFormat={
                    team?.settings?.outboundLabels?.weightFormat ??
                    WeightFormat.IMPERIAL
                  }
                />
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
      <RedoCheckbox
        description={
          <Flex>
            <RedoExternalLink
              text="Learn more"
              url="https://faq.usps.com/s/article/What-is-Media-Mail-Book-Rate"
            />
          </Flex>
        }
        label="USPS Media Mail eligible"
        setValue={handleMediaMailEligibility}
        size="xs"
        value={
          step.shippingOptions?.specialRateEligibility?.includes(
            SpecialRateEligibility.MEDIA_MAIL,
          ) ?? false
        }
      />
      {/* HIDE LIBRARY MAIL FOR NOW */}
      {/* <RedoCheckbox
        description={
          <Flex>
            <RedoExternalLink
              text="Learn more"
              url="https://faq.usps.com/s/article/What-are-Library-Mail-Service-and-Media-Mail-Service"
            />
          </Flex>
        }
        label="USPS Library Mail eligible"
        setValue={handleLibraryMailEligibility}
        size="xs"
        value={
          step.shippingOptions?.specialRateEligibility?.includes(
            SpecialRateEligibility.LIBRARY_MAIL,
          ) ?? false
        }
      /> */}
    </Flex>
  );
});
